import Phaser from 'phaser';
import { Alien } from './Enemy';
import { DinoId, DINOS } from '../data/dinos';
import { Sfx } from './Sfx';

const STATE_ENTRY = 0;
const STATE_ACTIVE = 1;
const STATE_DYING = 2;
const STATE_BURROWED = 3;

/**
 * Boss variants — one per planet:
 *   doux: rolling asteroid, horizontal patrol with sinusoidal bob
 *   mort: hovering magma orb, drops gravity-fed lava bombs straight down
 *   tard: burrowing sand worm, vanishes and reappears at random arena x
 *   vita: sky hunter, big arcing swoops between high altitude and ground
 */

/**
 * The classic "rolling asteroid" boss. Patrols horizontally inside an arena,
 * bobs vertically, occasionally spits an alien minion. Takes a fixed number
 * of bullet hits, then explodes in a particle shower.
 *
 * The GameScene is responsible for arena bounds, camera lock, and minion
 * collisions; this class just owns the boss's behaviour + HP.
 */
export class BossAsteroid extends Phaser.Physics.Arcade.Sprite {
  readonly maxHp: number;
  hp: number;
  readonly variant: DinoId;
  private state = STATE_ENTRY;
  private dead = false;
  /** Center of the arena horizontally — the boss patrols around this. */
  private arenaCenterX: number;
  private patrolHalfWidth = 110;
  private bobBaseY: number;
  private bobAmp = 8;
  private bobT = 0;
  private rollDir: 1 | -1 = 1;
  private nextSpawnAt = 0;
  /** Burrow timing — only used by tard variant. */
  private burrowEndAt = 0;
  /** Swoop progress in radians — only used by vita variant. */
  private swoopT = 0;
  /** Callback the GameScene wires up — fires when the boss spits a minion. */
  onSpitMinion?: (alien: Alien) => void;
  /** Callback the GameScene wires up — fires when the boss drops a bomb. */
  onDropBomb?: (x: number, y: number) => void;
  /** Callback fired exactly once when the boss dies. */
  onDefeated?: () => void;

  constructor(scene: Phaser.Scene, x: number, y: number, hp: number, variant: DinoId = 'doux') {
    // Each planet gets its own custom boss artwork (see PreloadScene).
    // Falls back to the generic asteroid if a variant texture is missing.
    const textureKey = scene.textures.exists(`boss-${variant}`) ? `boss-${variant}` : 'boss-asteroid';
    super(scene, x, y, textureKey);
    this.maxHp = hp;
    this.hp = hp;
    this.variant = variant;
    this.arenaCenterX = x;
    this.bobBaseY = y;
    // No tint applied — each variant texture already carries its own palette.
    void DINOS;

    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setOrigin(0.5, 0.5);

    // Drive the state machine off the scene's update event directly. The
    // scene's update() method override path proved unreliable in this build
    // (Phaser was calling something else), and Sprite.preUpdate registration
    // also got skipped. Subscribing to scene.events.on('update') is robust.
    scene.events.on('update', this.tick, this);
    this.once('destroy', () => {
      scene.events.off('update', this.tick, this);
    });

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    // Body deliberately taller than the visible 56×56 sprite — gives players
    // generous vertical leeway on jump-shots. The boss is a fixed-altitude
    // bobber, so a tight hitbox makes hits feel impossible. Wider hitbox =
    // hits feel like they LOOK like they're connecting.
    body.setSize(52, 64);
    body.setOffset(2, -4);

    // Each boss has its own idle motion that matches its silhouette.
    switch (variant) {
      case 'doux':
        // Frozen Comet — slow rotation, like a rock tumbling through space.
        scene.tweens.add({
          targets: this,
          angle: 360,
          duration: 6000,
          repeat: -1,
          ease: 'Linear',
        });
        break;
      case 'mort':
        // Magma Orb — ominous pulse, no spin (it has a fixed glowing core).
        scene.tweens.add({
          targets: this,
          scale: { from: 1, to: 1.08 },
          duration: 700,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        break;
      case 'tard':
        // Sand Worm — slight horizontal sway, like its body is breathing.
        scene.tweens.add({
          targets: this,
          scaleX: { from: 1, to: 0.96 },
          duration: 800,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        break;
      case 'vita':
        // Sky Hunter — wing-flap via a vertical squash.
        scene.tweens.add({
          targets: this,
          scaleY: { from: 1, to: 0.85 },
          duration: 350,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        break;
    }

    this.nextSpawnAt = scene.time.now + 2400;
  }

  isDead() {
    return this.dead;
  }

  /** Bullet hit — bullets always do exactly 1 damage to the boss. */
  takeBulletHit(_fromX: number) {
    if (this.dead) return;
    this.hp -= 1;
    Sfx.bossHit();
    this.setTint(0xffffff);
    this.scene.time.delayedCall(80, () => {
      if (!this.dead && this.scene) this.clearTint();
    });
    // Phase-2 trigger: at half HP, the boss enrages.
    if (!this.phase2 && this.hp <= Math.floor(this.maxHp / 2)) {
      this.enterPhase2();
    }
    if (this.hp <= 0) this.die();
  }

  private phase2 = false;

  /** Halfway-down escalation — pure speed boost per variant. */
  private enterPhase2() {
    this.phase2 = true;
    Sfx.bossHit();
    // Brief flash + camera punch through the scene's camera.
    this.scene.cameras.main.shake(220, 0.006);
    this.scene.tweens.add({
      targets: this,
      scale: { from: 1.25, to: 1 },
      duration: 280,
      ease: 'Back.easeOut',
    });
    // Tint pulse to red briefly so the player FEELS the phase shift.
    this.setTint(0xff3a4a);
    this.scene.time.delayedCall(220, () => {
      if (!this.dead && this.scene) this.clearTint();
    });
  }

  /** Stomp from above — same damage as a bullet, no ammo cost. */
  takeStomp() {
    if (this.dead) return;
    this.takeBulletHit(this.x);
  }

  private die() {
    if (this.dead) return;
    this.dead = true;
    this.state = STATE_DYING;
    Sfx.bossDie();

    // Quick flashing flicker before exploding.
    this.scene.tweens.add({
      targets: this,
      alpha: { from: 0.2, to: 1 },
      duration: 60,
      yoyo: true,
      repeat: 6,
    });

    // Big particle burst.
    const cx = this.x;
    const cy = this.y;
    for (let i = 0; i < 28; i++) {
      const ang = (i / 28) * Math.PI * 2;
      const speed = Phaser.Math.Between(60, 220);
      const p = this.scene.add
        .image(cx, cy, 'star')
        .setScale(Phaser.Math.FloatBetween(0.6, 1.6))
        .setDepth(900);
      this.scene.tweens.add({
        targets: p,
        x: cx + Math.cos(ang) * speed,
        y: cy + Math.sin(ang) * speed,
        alpha: 0,
        scale: 0.2,
        duration: 700,
        ease: 'Cubic.easeOut',
        onComplete: () => p.destroy(),
      });
    }
    // Camera shake handled by GameScene if it wants.
    this.scene.time.delayedCall(450, () => {
      this.destroy();
      this.onDefeated?.();
    });
  }

  /**
   * Driven manually from GameScene.update. We bypass arcade-physics velocity
   * entirely — the body's position auto-syncs with the sprite position at
   * the start of each frame, so direct x/y assignment still produces proper
   * overlap collisions with bullets and the dino.
   */
  tick(_t: number, dt: number) {
    if (this.dead) return;
    this.bobT += dt;
    const dtSec = dt / 1000;

    if (this.state === STATE_ENTRY) {
      // Drift in from above until close to the patrol band.
      this.y += 140 * dtSec;
      if (this.y >= this.bobBaseY - 4) {
        this.y = this.bobBaseY;
        this.state = STATE_ACTIVE;
      }
      return;
    }

    switch (this.variant) {
      case 'mort':
        this.tickMort(dtSec);
        break;
      case 'tard':
        this.tickTard(dtSec);
        break;
      case 'vita':
        this.tickVita(dtSec);
        break;
      case 'doux':
      default:
        this.tickDoux(dtSec);
        break;
    }
  }

  /** Doux — classic rolling asteroid with bob + minion spit. */
  private tickDoux(dtSec: number) {
    const speed = this.phase2 ? 100 : 60;
    const targetY = this.bobBaseY + Math.sin(this.bobT / 280) * this.bobAmp;
    this.y += (targetY - this.y) * Math.min(1, dtSec * 6);
    this.x += this.rollDir * speed * dtSec;
    if (this.x > this.arenaCenterX + this.patrolHalfWidth) this.rollDir = -1;
    else if (this.x < this.arenaCenterX - this.patrolHalfWidth) this.rollDir = 1;
    if (this.phase2) this.maybeSpitAlien(1100, 1900);
    else this.maybeSpitAlien(2200, 3400);
  }

  /** Mort — hovers near center, drops gravity bombs straight down. */
  private tickMort(dtSec: number) {
    // Slow drift around center (no horizontal patrol).
    const targetX = this.arenaCenterX + Math.sin(this.bobT / 600) * 60;
    const targetY = this.bobBaseY + Math.sin(this.bobT / 320) * 12;
    this.x += (targetX - this.x) * Math.min(1, dtSec * 3);
    this.y += (targetY - this.y) * Math.min(1, dtSec * 3);

    if (this.scene.time.now >= this.nextSpawnAt && this.onDropBomb) {
      const min = this.phase2 ? 600 : 1400;
      const max = this.phase2 ? 1100 : 2200;
      this.nextSpawnAt = this.scene.time.now + Phaser.Math.Between(min, max);
      this.onDropBomb(this.x, this.y + 24);
    }
  }

  /** Tard — burrows in and out, reappears at random arena x. */
  private tickTard(dtSec: number) {
    if (this.state === STATE_BURROWED) {
      // Stay underground for the duration, then re-emerge.
      if (this.scene.time.now >= this.burrowEndAt) {
        // Reappear at a random arena x with a vertical pop-in.
        const newX = this.arenaCenterX + Phaser.Math.Between(-80, 80);
        this.x = newX;
        this.y = this.bobBaseY + 60;
        this.setAlpha(1);
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.checkCollision.none = false;
        // Scene-time tween up to home.
        this.scene.tweens.add({
          targets: this,
          y: this.bobBaseY,
          duration: 250,
          ease: 'Cubic.easeOut',
        });
        this.state = STATE_ACTIVE;
        this.nextSpawnAt = this.scene.time.now + 1800;
      }
      return;
    }
    // Active: standard rolling, faster in phase 2.
    const speed = this.phase2 ? 130 : 90;
    const targetY = this.bobBaseY + Math.sin(this.bobT / 280) * 6;
    this.y += (targetY - this.y) * Math.min(1, dtSec * 6);
    this.x += this.rollDir * speed * dtSec;
    if (this.x > this.arenaCenterX + this.patrolHalfWidth) this.rollDir = -1;
    else if (this.x < this.arenaCenterX - this.patrolHalfWidth) this.rollDir = 1;
    this.maybeSpitAlien(this.phase2 ? 800 : 1500, this.phase2 ? 1300 : 2200);

    // Trigger a burrow occasionally. Phase 2: tighter cadence.
    if (this.scene.time.now > this.nextBurrowAt) {
      const min = this.phase2 ? 2200 : 4500;
      const max = this.phase2 ? 3500 : 6500;
      this.nextBurrowAt = this.scene.time.now + Phaser.Math.Between(min, max);
      this.startBurrow();
    }
  }

  private nextBurrowAt = 0;

  private startBurrow() {
    this.state = STATE_BURROWED;
    this.burrowEndAt = this.scene.time.now + 1300;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.checkCollision.none = true;
    // Sink into the ground visually.
    this.scene.tweens.add({
      targets: this,
      y: this.bobBaseY + 60,
      alpha: 0.15,
      duration: 280,
      ease: 'Cubic.easeIn',
    });
  }

  /** Vita — flies in big arcing swoops, no minions, body itself is the attack. */
  private tickVita(dtSec: number) {
    // Arcing motion — large horizontal sweep with ducking dive every cycle.
    // Phase 2 nearly doubles the swoop frequency — very aggressive.
    this.swoopT += dtSec * (this.phase2 ? 1.6 : 0.9);
    const ax = this.arenaCenterX + Math.sin(this.swoopT) * (this.patrolHalfWidth + 20);
    // Dive low when sin is near 0 (passing through center), high at extremes.
    const phaseY = Math.abs(Math.cos(this.swoopT)); // 1 at extremes, 0 at center
    const ay = this.bobBaseY - phaseY * 30 + (1 - phaseY) * 60;
    // Apply.
    this.x += (ax - this.x) * Math.min(1, dtSec * 5);
    this.y += (ay - this.y) * Math.min(1, dtSec * 5);
    // Face the direction we're moving.
    this.setFlipX(Math.cos(this.swoopT) < 0);
  }

  private maybeSpitAlien(minMs: number, maxMs: number) {
    if (this.scene.time.now >= this.nextSpawnAt && this.onSpitMinion) {
      this.nextSpawnAt = this.scene.time.now + Phaser.Math.Between(minMs, maxMs);
      // Use the planet-themed minion texture so a spat alien matches the
      // ground-spawned ones (frosty on Glacier, embers on Inferno, etc).
      const a = new Alien(this.scene, this.x, this.y + 20, undefined, `alien-${this.variant}`);
      a.walkDir = this.x > this.arenaCenterX ? -1 : 1;
      this.onSpitMinion(a);
    }
  }
}
